import React, { useEffect, useState } from "react";
import axios from "axios";
import CardTable from "../../../components/AdminTable/CardTable";
import { apiKey } from "../../../config/config";
import { toast } from "react-toastify";
import { FaCog, FaEye } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import dayjs from "dayjs";
import { languageHelper } from "../../../config/helperFunctions";

const STATUS_LABELS = {
  pending: "Pending",
  assigned: "Assigned",
  picked: "Picked",
  on_the_way: "On The Way",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

function RiderOrder() {
  const [deliveries, setDeliveries] = useState([]);
  const [error, setError] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const columns = [
    {
      header: languageHelper("SN") || "SN",
      cell: (row) => row.row.index + 1,
    },
    {
      header: languageHelper("Created At") || "Created At",
      accessorKey: "formatted_created_at",
    },
    {
      header: languageHelper("Order No") || "Order No",
      accessorKey: "sale_no",
    },
    {
      header: languageHelper("Customer") || "Customer",
      accessorKey: "customer",
      cell: (row) => (
        <span
          onClick={() => setSelectedCustomer(row.getValue())}
          className="text-blue-600 cursor-pointer underline"
        >
          {row.getValue()?.name || "N/A"}
        </span>
      ),
    },
    {
      header: languageHelper("Rider") || "Rider",
      accessorKey: "rider",
      cell: (row) => row.getValue()?.name || "N/A",
    },
    {
      header: languageHelper("Total") || "Total",
      accessorKey: "total_amount",
    },
    {
      header: languageHelper("Status") || "Status",
      accessorKey: "status",
      cell: (row) => (
        <span
          className={`px-3 py-1 rounded-full text-xs capitalize ${row.getValue() === "delivered"
              ? "bg-green-100 text-green-700"
              : row.getValue() === "cancelled"
                ? "bg-red-100 text-red-700"
                : "bg-gray-100 text-gray-700"
            }`}
        >
          {languageHelper(STATUS_LABELS[row.getValue()] || row.getValue())}
        </span>
      ),
    },
    {
      header: languageHelper("Actions") || "Actions",
      cell: (row) => (
        <ActionDropdown
          order={row.row.original}
          updateStatus={updateStatus}
          setSelectedOrder={setSelectedOrder}
          setShowCancelModal={setShowCancelModal}
        />
      ),
    },
  ];

  // Fetch Deliveries
  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiKey}/api/rider-deliveries`);
      const data = (res.data.data || []).map((d) => ({
        ...d,
        formatted_created_at: dayjs(d.created_at).format("DD MMM YYYY, hh:mm A"),
      }));
      setDeliveries(data);
    } catch (err) {
      setError("Error loading rider deliveries");
    } finally {
      setLoading(false);
    }
  };

  // Update Delivery Status
  const updateStatus = async (id, newStatus, reason = "") => {
    try {
      const res = await axios.post(
        `${apiKey}/api/rider-deliveries/${id}/update-status`,
        {
          status: newStatus,
          cancel_reason: reason,
        }
      );
      toast.success(res.data.message || "Status updated", {
        theme: "colored",
      });
      fetchDeliveries();
      setShowCancelModal(false);
      setCancelReason("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status", {
        theme: "colored",
      });
    }
  };

  // The order/invoice itself closes automatically the moment the rider
  // marks the delivery delivered -- this is the separate step for when
  // the rider physically hands the collected cash back: it's what
  // actually records the payment (paid_amount/multiple_payments) so it
  // reconciles correctly in the shift/register report and everywhere
  // else that reads those fields.
  const confirmPayment = async (id) => {
    setConfirmingPayment(true);
    try {
      const res = await axios.post(
        `${apiKey}/api/rider-deliveries/${id}/confirm-payment`
      );
      toast.success(res.data.message || "Payment confirmed", {
        theme: "colored",
      });
      fetchDeliveries();
      setSelectedOrder((prev) =>
        prev ? { ...prev, payment_status: "paid" } : prev
      );
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to confirm payment",
        { theme: "colored" }
      );
    } finally {
      setConfirmingPayment(false);
    }
  };

  // Confirm Cancel
  const confirmCancel = async () => {
    if (!cancelReason.trim()) {
      toast.warning("Please enter a cancel reason");
      return;
    }
    updateStatus(selectedOrder.id, "cancelled", cancelReason);
  };

  useEffect(() => {
    fetchDeliveries();

    // Listen to cancel modal event from ActionDropdown
    const handleOpenCancel = (e) => {
      setSelectedOrder(e.detail.order);
      setShowCancelModal(true);
    };
    document.addEventListener("openCancelModal", handleOpenCancel);
    return () => document.removeEventListener("openCancelModal", handleOpenCancel);
  }, []);

  return (
    <main>
      <CardTable
        data={deliveries}
        columns={columns}
        heading={languageHelper("Rider Deliveries")}
        error={error}
      />

      {loading && (
        <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
          <img src="/assets/gif/dash2.gif" alt="Loading..." />
        </div>
      )}

      {/* ORDER DETAILS MODAL */}
      {selectedOrder && !showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-[550px] relative">
            <button
              onClick={() => setSelectedOrder(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
            >
              ✕
            </button>
            <h2 className="text-xl text-[#7B20C7] mb-3">
              Order Details — #{selectedOrder.sale_no}
            </h2>
            <div className="space-y-2 text-sm">
              <p><b>Customer:</b> {selectedOrder.customer?.name}</p>
              <p><b>Rider:</b> {selectedOrder.rider?.name}</p>
              <p><b>Total:</b> {selectedOrder.total_amount}</p>
              {(selectedOrder.delivery_address || (selectedOrder.delivery_lat && selectedOrder.delivery_lng)) && (
                <p>
                  <b>Address:</b>{" "}
                  <a
                    href={
                      selectedOrder.delivery_lat && selectedOrder.delivery_lng
                        ? `https://www.google.com/maps/search/?api=1&query=${selectedOrder.delivery_lat},${selectedOrder.delivery_lng}`
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedOrder.delivery_address)}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {selectedOrder.delivery_address || "View on map"}
                  </a>
                </p>
              )}
              <p><b>Distance (km):</b> {selectedOrder.distance_km}</p>
              <p><b>Status:</b> {selectedOrder.status}</p>
              <p className="flex items-center gap-2">
                <b>Payment:</b> {selectedOrder.payment_status}
                {selectedOrder.status === "delivered" &&
                  selectedOrder.payment_status !== "paid" && (
                    <button
                      onClick={() => confirmPayment(selectedOrder.id)}
                      disabled={confirmingPayment}
                      className="bg-green-600 text-white text-xs px-3 py-1.5 rounded-full hover:bg-green-700 disabled:opacity-60"
                    >
                      {confirmingPayment
                        ? "Confirming..."
                        : "Payment Received from Rider"}
                    </button>
                  )}
              </p>
              {selectedOrder.cancel_reason && (
                <p className="text-red-500">
                  <b>Cancel Reason:</b> {selectedOrder.cancel_reason}
                </p>
              )}
              <p><b>Remarks:</b> {selectedOrder.remarks || "—"}</p>
              <p>
                <b>Created At:</b>{" "}
                {dayjs(selectedOrder.created_at).format("DD MMM YYYY, hh:mm A")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL MODAL */}
      {showCancelModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-[400px] relative">
            <h3 className="text-lg text-red-600 font-semibold mb-3">
              Cancel Order #{selectedOrder?.sale_no}
            </h3>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder={languageHelper("Enter cancel reason...")}
              className="w-full border p-2 rounded mb-3 h-24 resize-none"
            ></textarea>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCancelModal(false)}
                className="bg-gray-300 px-4 py-2 rounded"
              >
                {languageHelper("Close")}
              </button>
              <button
                onClick={confirmCancel}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                {languageHelper("Confirm Cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMER DETAILS MODAL */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-[600px] relative overflow-y-auto max-h-[80vh]">
            <button
              onClick={() => setSelectedCustomer(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
            >
              ✕
            </button>
            <h2 className="text-xl text-[#7B20C7] mb-4">
              Customer Details — {selectedCustomer.name}
            </h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <p><b>Name:</b> {selectedCustomer.name}</p>
              <p><b>Email:</b> {selectedCustomer.email}</p>
              <p><b>Phone:</b> {selectedCustomer.phone}</p>
              <p><b>Address:</b> {selectedCustomer.address}</p>
              <p><b>Company:</b> {selectedCustomer.company_name}</p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

// Dropdown Component
function ActionDropdown({ order, updateStatus, setSelectedOrder, setShowCancelModal }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const statuses = [
    { label: languageHelper("Pending"), value: "pending" },
    { label: languageHelper("Assigned"), value: "assigned" },
    { label: languageHelper("Picked"), value: "picked" },
    { label: languageHelper("On The Way"), value: "on_the_way" },
    { label: languageHelper("Delivered"), value: "delivered" },
    { label: languageHelper("Cancelled"), value: "cancelled" },
  ];

  return (
    <div className="relative">
      <button
        className="text-blueGray-500 px-3 flex items-center justify-between gap-1 border rounded-full"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <FaCog className="text-d-bg-text" />
        <IoIosArrowDown className="text-d-bg-text" />
      </button>
      <div
        className={`${showDropdown ? "block" : "hidden"
          } absolute top-4 left-0 -translate-x-[75%] bg-white text-base z-10 py-2 list-none rounded shadow-lg min-w-40`}
      >
        <button
          onClick={() => {
            setSelectedOrder(order);
            setShowDropdown(false);
          }}
          className="text-sm py-2 px-4 font-normal flex items-center gap-3 w-full hover:bg-gray-100"
        >
          <FaEye /> {languageHelper("View Details")}
        </button>
        <hr />
        {statuses.map((st) => (
          <button
            key={st.value}
            onClick={() => {
              if (st.value === "cancelled") {
                setSelectedOrder(order);
                setShowCancelModal(true);
              } else {
                updateStatus(order.id, st.value);
              }
              setShowDropdown(false);
            }}
            className={`text-sm py-2 px-4 w-full text-left hover:bg-[#7B20C7] hover:text-white ${order.status === st.value ? "bg-green-100 text-green-700" : ""
              }`}
          >
            {st.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default RiderOrder;
